import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PatientPicker } from "@/components/PatientPicker";

const EMPTY = { medication: "", dose: "", frequency: "", notes: "" };

// mode: "add" | "edit" | "link"
export const MedicationDialog = ({ open, onOpenChange, mode, med }) => {
  const qc = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [patient, setPatient] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(med ? { medication: med.medication, dose: med.dose || "", frequency: med.frequency || "", notes: med.notes || "" } : EMPTY);
      setPatient(null);
    }
  }, [open, med]);

  const save = useMutation({
    mutationFn: () => {
      if (mode === "link") return api.post(`/medications/${med.id}/link`, { patient_id: patient?.id });
      if (mode === "edit") return api.put(`/medications/${med.id}`, form);
      return api.post("/medications", { ...form, patient_id: patient?.id });
    },
    onSuccess: () => {
      toast.success(mode === "link" ? "Matched to patient" : mode === "edit" ? "Medication updated" : "Medication added");
      qc.invalidateQueries({ queryKey: ["medications"] });
      onOpenChange(false);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save"),
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const needsPatient = mode !== "edit";
  const canSave = (!needsPatient || patient) && (mode === "link" || form.medication.trim());
  const title = { add: "Add medication", edit: "Edit medication", link: "Match to patient" }[mode];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="medication-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {mode === "link" ? `From ${med?.note ? "the sheet" : "your file"}: “${med?.source_name || "no name"}” — ${med?.medication} ${med?.dose || ""}. Other unmatched entries with the same name are matched too.` : "Medicine details are kept in the CRM only — they are not sent to DrChrono."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          {needsPatient && <div className="space-y-1.5"><Label>Patient</Label>
            <PatientPicker value={patient} onChange={setPatient} initial={mode === "link" ? (med?.source_name || "").split(",").map((s) => s.trim()).reverse().join(" ").trim() : ""} /></div>}
          {mode !== "link" && (
            <>
              <div className="space-y-1.5"><Label>Medicine</Label><Input data-testid="med-input-medication" value={form.medication} onChange={set("medication")} placeholder="e.g. Testosterone cypionate" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Dose</Label><Input data-testid="med-input-dose" value={form.dose} onChange={set("dose")} placeholder="e.g. 200 mg/mL, 0.5 mL" /></div>
                <div className="space-y-1.5"><Label>How often</Label><Input data-testid="med-input-frequency" value={form.frequency} onChange={set("frequency")} placeholder="e.g. Weekly" /></div>
              </div>
              <div className="space-y-1.5"><Label>Notes</Label><Textarea data-testid="med-input-notes" rows={2} value={form.notes} onChange={set("notes")} /></div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="med-cancel-btn">Cancel</Button>
          <Button data-testid="med-save-btn" disabled={!canSave || save.isPending} onClick={() => save.mutate()}
            className="bg-[#0B1F44] hover:bg-[#13306a]">{save.isPending ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
